import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Code, Eye } from 'lucide-react';

interface EmailTemplate {
  id: string;
  name: string;
  description: string;
  type: 'signup' | 'password-reset' | 'confirmation' | 'invitation';
  functionName: string;
  status: 'active' | 'inactive';
}

interface EmailTemplateViewerProps {
  template: EmailTemplate;
}

export const EmailTemplateViewer = ({ template }: EmailTemplateViewerProps) => {
  const getTemplatePreview = () => {
    switch (template.type) {
      case 'confirmation':
        return {
          subject: 'Confirm your Sports IQs account',
          html: `
            <div style="max-width: 580px; margin: 0 auto; padding: 20px 0 48px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
              <h1 style="color: #333; font-size: 24px; font-weight: bold; margin: 40px 0;">Welcome to Sports IQs!</h1>
              <p style="color: #333; font-size: 16px; line-height: 26px; margin: 24px 0;">Hi [firstName],</p>
              <p style="color: #333; font-size: 16px; line-height: 26px; margin: 24px 0;">
                Thanks for signing up for Sports IQs! We're excited to have you join our community.
                To get started, please confirm your email address by clicking the button below.
              </p>
              <a href="[confirmationUrl]" style="background-color: #007ee6; color: white; padding: 12px 20px; text-decoration: none; border-radius: 3px; display: block; text-align: center; margin: 24px 0; font-size: 16px;">
                Confirm Your Account
              </a>
              <p style="color: #333; font-size: 16px; line-height: 26px; margin: 24px 0;">
                If the button above doesn't work, you can copy and paste this link into your browser:
              </p>
              <p style="color: #007ee6; font-size: 14px; word-break: break-all; text-decoration: underline;">[confirmationUrl]</p>
              <p style="color: #666; font-size: 14px; line-height: 22px; margin: 24px 0;">
                This confirmation link will expire in 24 hours. If you didn't create an account with Sports IQs, 
                you can safely ignore this email.
              </p>
              <p style="color: #666; font-size: 14px; line-height: 24px; margin-top: 48px;">
                Best regards,<br>
                The Sports IQs Team
              </p>
            </div>
          `
        };
      
      case 'password-reset':
        return {
          subject: 'Reset your Sports IQs password',
          html: `
            <div style="max-width: 580px; margin: 0 auto; padding: 20px 0 48px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
              <h1 style="color: #333; font-size: 24px; font-weight: bold; margin: 40px 0;">Reset Your Password</h1>
              <p style="color: #333; font-size: 16px; line-height: 26px; margin: 24px 0;">
                You requested to reset your password for your Sports IQs account. Click the button below to set a new password.
              </p>
              <a href="[resetUrl]" style="background-color: #007ee6; color: white; padding: 12px 20px; text-decoration: none; border-radius: 3px; display: block; text-align: center; margin: 24px 0; font-size: 16px;">
                Reset Password
              </a>
              <p style="color: #333; font-size: 16px; line-height: 26px; margin: 24px 0;">
                If the button above doesn't work, you can copy and paste this link into your browser:
              </p>
              <p style="color: #007ee6; font-size: 14px; word-break: break-all; text-decoration: underline;">[resetUrl]</p>
              <p style="color: #666; font-size: 14px; line-height: 22px; margin: 24px 0;">
                This link will expire in 24 hours for security reasons. If you didn't request this password reset, 
                you can safely ignore this email.
              </p>
              <p style="color: #666; font-size: 14px; line-height: 24px; margin-top: 48px;">
                Best regards,<br>
                The Sports IQs Team
              </p>
            </div>
          `
        };
      
      case 'invitation':
        return {
          subject: "You're invited to join Sports IQs",
          html: `
            <div style="max-width: 580px; margin: 0 auto; padding: 20px 0 48px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
              <h1 style="color: #333; font-size: 24px; font-weight: bold; margin: 40px 0;">This isn't just any invite…</h1>
              <p style="color: #333; font-size: 16px; line-height: 26px; margin: 24px 0;">Hi [firstName],</p>
              <p style="color: #333; font-size: 16px; line-height: 26px; margin: 24px 0;">You've been invited to join Sports IQs as a [role].</p>
              <p style="color: #333; font-size: 16px; line-height: 26px; margin: 24px 0;">Click the button below to complete your registration:</p>
              <a href="[inviteUrl]" style="background-color: #007ee6; color: white; padding: 12px 20px; text-decoration: none; border-radius: 3px; display: block; text-align: center; margin: 24px 0; font-size: 16px;">
                Accept Invitation
              </a>
              <p style="color: #333; font-size: 16px; line-height: 26px; margin: 24px 0;">
                If the button above doesn't work, you can copy and paste this link into your browser:
              </p>
              <p style="color: #007ee6; font-size: 14px; word-break: break-all; text-decoration: underline;">[inviteUrl]</p>
              <p style="color: #666; font-size: 14px; line-height: 22px; margin: 24px 0;">
                Heads up: This invite expires in 7 days — don't sleep on it.
              </p>
              <p style="color: #666; font-size: 14px; line-height: 24px; margin-top: 48px;">
                Best regards,<br>
                The Sports IQs Team
              </p>
            </div>
          `
        };
      
      default:
        return {
          subject: 'Sports IQs Notification',
          html: '<p>Template preview not available</p>'
        };
    }
  };

  const preview = getTemplatePreview();

  const getVariables = () => {
    switch (template.type) {
      case 'confirmation':
        return ['firstName', 'confirmationUrl'];
      case 'password-reset':
        return ['resetUrl'];
      case 'invitation':
        return ['firstName', 'role', 'inviteUrl'];
      default:
        return [];
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">{template.name}</h3>
          <p className="text-sm text-muted-foreground">{template.description}</p>
        </div>
        <Badge variant="outline">{template.functionName}</Badge>
      </div>

      <Tabs defaultValue="preview" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="preview" className="flex items-center gap-2">
            <Eye className="h-4 w-4" />
            Preview
          </TabsTrigger>
          <TabsTrigger value="variables" className="flex items-center gap-2">
            <Code className="h-4 w-4" />
            Variables
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="preview" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">Subject Line</CardTitle>
            </CardHeader>
            <CardContent>
              <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                {preview.subject}
              </code>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">Email Preview</CardTitle>
              <CardDescription>
                This is how the email will appear to recipients
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div 
                className="border rounded-lg p-4 bg-white"
                dangerouslySetInnerHTML={{ __html: preview.html }}
              />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="variables" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">Template Variables</CardTitle>
              <CardDescription>
                Variables that are dynamically replaced in the email template
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {getVariables().map((variable) => (
                  <div key={variable} className="flex items-center space-x-2">
                    <code className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded">
                      [{variable}]
                    </code>
                    <span className="text-sm text-muted-foreground">
                      Replaced with actual {variable.replace(/([A-Z])/g, ' $1').toLowerCase()}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};